import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

// Libs
import type { PlatformLanguagesType } from '@/types/userType';

import 'dayjs/locale/es';
import 'dayjs/locale/fr';

export const DATETIME_FORMAT_SCHEMAS = {
    month_day_year: 'MM/DD/YYYY',
    day_month_year: 'DD/MM/YYYY',
    year_month_day: 'YYYY/MM/DD',
    utc: 'UTC Z',
    '12': 'hh:mm:ss a',
    '24': 'HH:mm:ss',
    '24_hour_minutes': 'HH:mm',
    '12_hour_minutes': 'hh:mm a',
    '12_noSeconds': 'h:mm a',
    '24_noSeconds': 'H:mm',
    month_day_year_inLetters_noYear: 'dddd MMM. DD',
    day_month_year_inLetters_noYear: 'dddd DD MMMM',
    year_month_day_inLetters_noYear: 'dddd MMM. DD',
    month_day_year_inLetters: 'MMM. DD, YYYY',
    day_month_year_inLetters: 'DD MMM. YYYY',
    year_month_day_inLetters: 'YYYY MMM. DD',
    month_day_year_noYear: 'MMM, DD',
    day_month_year_noYear: 'DD MMM',
    year_month_day_noYear: 'MMM, DD',
    month_day_year_complete: 'MMMM ddd DD YYYY',
    day_month_year_complete: 'ddd DD MMMM YYYY',
    year_month_day_complete: 'YYYY MMMM ddd DD',
    month_day_year_split_noYear: 'MMM. ddd. DD',
    day_month_year_split_noYear: 'ddd. DD MMM.',
    year_month_day_split_noYear: 'MMM. ddd. DD',
    month_day_year_split: 'MMM. DD, YYYY',
    day_month_year_split: 'DD MMM. YYYY',
    year_month_day_split: 'YYYY, MMM. DD',
    month_day_year_24_noSeconds_date: 'MM/DD/YYYY H:mm',
    day_month_year_24_noSeconds_date: 'DD/MM/YYYY H:mm',
    year_month_day_24_noSeconds_date: 'YYYY/MM/DD H:mm',
    weekDay_day_month_year_hour_minute: 'ddd DD/MM/YY H:mm A'
} as const;

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);

const useFormatDateTime = (timezone: string, locale: PlatformLanguagesType = 'en') => {
    // Format date with timezone on specific format
    const formatDateTime = (datetime: string, format: keyof typeof DATETIME_FORMAT_SCHEMAS) => {
        try {
            return (
                datetime &&
                dayjs(datetime)
                    .utc()
                    .tz(timezone)
                    .locale(locale)
                    .format(DATETIME_FORMAT_SCHEMAS[format])
            );
        } catch (error) {
            console.error('[useFormatDateTime] Error formatting date:', error);
            return '';
        }
    };

    // Format date without timezone on specific format
    const formatDateTimeNoTz = (datetime: string, format: keyof typeof DATETIME_FORMAT_SCHEMAS) => {
        try {
            return (
                datetime &&
                dayjs(datetime).utc().tz().locale(locale).format(DATETIME_FORMAT_SCHEMAS[format])
            );
        } catch (error) {
            console.error('[useFormatDateTime] Error formatting date:', error);
            return '';
        }
    };

    // Return current date with timezone
    const formatFromNow = (datetime: string) => {
        try {
            return dayjs(datetime).utc().tz(timezone).locale(locale).fromNow();
        } catch (error) {
            console.error('[useFormatDateTime] Error formatting date:', error);
            return '';
        }
    };

    // Return current date with timezone
    const getNow = () => {
        try {
            return dayjs().utc().tz(timezone).format();
        } catch (error) {
            console.error('[useFormatDateTime] Error getting now:', error);
            return dayjs(new Date()).utc().tz(timezone).format();
        }
    };

    return { formatDateTime, formatFromNow, formatDateTimeNoTz, getNow };
};

export default useFormatDateTime;
